import axios from 'axios';
import { fromBlob } from 'image-resize-compress';
import { API_INSTANCE } from './features/utils';

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const isValidUrl = (urlString: string) => {
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

export const getPresignedURL = async (
  file: File,
  presignedUrl = `/api/v1/dashboard/events/presigned-url`
): Promise<string> => {
  if (isValidUrl(file)) return file;
  if (isValidUrl(file?.name)) return file.name;

  // Get the file extension from the filename
  const getFileExtension = (filename: string) => {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
  };

  // Extract the extension from the original file name
  const fileExtension = getFileExtension(file.name);

  // Request presigned URL, passing the original filename with extension
  const { data } = await API_INSTANCE.post(presignedUrl, {
    name: file.name,
    extension: fileExtension, // Make sure extension is included in the request
  });

  // Check if we need to resize the image
  const originalImageTooLarge = file.size / 1000 > 2000;

  const quality = 90;
  const width = 1000;
  const height = 'auto';
  const format = fileExtension.toLowerCase() === 'gif' ? 'gif' : 'webp'; // Keep GIFs as GIFs

  // Process the file (compress if needed)
  const compressedFile = await fromBlob(file, quality, width, height, format);

  // Determine which file to upload (original or compressed)
  let fileToUpload = file.name.endsWith('.gif') || !originalImageTooLarge ? file : compressedFile;

  // Use UUID-based filename to avoid special character issues
  if (data.image_id) {
    // Create a filename with UUID but preserve the original extension
    const fileNameWithId = `${data.image_id}.${fileExtension}`;

    // Log the transformation for debugging
    console.log(`Transformed file name from "${file.name}" to "${fileNameWithId}"`);

    // Create a new File object with the UUID name but preserved extension
    fileToUpload = new File([fileToUpload], fileNameWithId, { type: file.type });
  }

  // Upload the file to the presigned URL
  await axios.put(data.presigned_url, fileToUpload, {
    headers: {
      'Content-Type': file.type,
    },
  });

  // Return the clean image URL provided by the API
  // This URL should include the UUID and preserve the original extension
  return data.image_url;
};

export function getParamsAfterHash(url) {
  if (typeof url !== 'string' || !url) url = location.href;
  url = url.split('#')[1];
  if (!url) return {};
  url = url.split('?')[1];
  if (!url) return {};
  return url.split('&').reduce(function (result, param) {
    const [key, value] = param.split('=');
    result[key] = value;
    return result;
  }, {});
}
